import ReactPlayer from "react-player"
import styled, { css } from "styled-components"
import { Annotation, Quote } from "../general-styles.component"
import { PrimaryHeading, SecondaryHeading } from "../heading.component"
import { Image } from "../image.component"
import {
  BaseLink,
  BasicButtonLink,
} from "../../../ui/components/link/link.component"
import { NewsletterContainer } from "../../newsletter/newsletter.component"
import { NewsletterBuilder } from "../../../types"

// Partials
export const SectionArticle = styled.article<{
  area?: "Left" | "Right" | string
}>`
  grid-area: ${({ area }) => (area ? area : "Article")};
  align-self: center;
  z-index: 1;
  white-space: pre-wrap;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  ${PrimaryHeading} {
    margin-top: 0;
    margin-bottom: ${43 / 30}em;

    &:first-child {
    }

    + p {
      margin-top: -${30 / 14}em;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      margin-bottom: ${83 / 37}em;

      + p {
        margin-top: -${60 / 16}em;
      }
    }
  }

  ${SecondaryHeading} {
    &:first-child {
      margin-top: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      /* margin-bottom: ${30 / 23}em; */
    }
  }

  ${BasicButtonLink} {
    /* height: 50px; */
    /* line-height: 50px; */

    @media (max-width: 375px) {
      width: 100%;
      text-align: center;
      box-sizing: border-box;
    }
  }

  p {
    white-space: pre-wrap;
  }

  p + a {
    margin-top: 2em;
    display: inline-block;
  }

  p + ${SecondaryHeading} {
    margin-top: ${50 / 23}em;
  }

  strong {
    font-weight: 500;
  }

  table {
    border-collapse: collapse;
  }

  table,
  th,
  tr,
  td {
    border: 1px solid black;
    padding: 0.5rem;
  }
  li {
    word-wrap: break-word;
  }
`

export const LinkedSectionImage = styled(BaseLink)`
  grid-area: Image;
  text-decoration: none;
`

export const SectionImage = styled(Image)<{
  area?: "Left" | "Right" | string
  disableShadow?: boolean
  contain?: boolean
}>`
  margin: 0;
  width: 100%;
  height: auto;
  overflow: hidden;
  border-radius: 5px;
  align-self: stretch;
  grid-area: ${({ area }) => (area ? area : "Image")};
  z-index: -1;

  ${({ disableShadow }) =>
    !disableShadow &&
    css`
      box-shadow: 0 2px 20px 0 ${({ theme }) => theme.color.lightShadow};
    `}

  img {
    object-fit: ${({ contain }) => (contain ? "contain" : "cover")};
    object-position: center;
  }
`

export const SectionVideo = styled(ReactPlayer)<{
  disableShadow?: boolean
}>`
  margin: 0;
  width: 100%;
  height: auto;
  position: relative;
  padding-bottom: 60%;
  overflow: hidden;
  border-radius: 5px;
  grid-area: Image;

  ${({ disableShadow }) =>
    !disableShadow &&
    css`
      box-shadow: 0 2px 20px 0 ${({ theme }) => theme.color.lightShadow};
    `}

  > div {
    top: 0;
    left: 0;
    position: absolute;
  }
`

export const SectionImageDescription = styled(Annotation)`
  grid-area: ImageDescription;
  margin-top: calc(-50px - 1em);
  opacity: 0.66;
  line-height: ${18 / 10}em;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    margin-top: 10px;
  }
`

export const SectionImageBelowDescription = styled(SectionImageDescription)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    margin-top: 30px;
  }
`

export const SectionHeading = styled.div`
  grid-area: Heading;
  justify-self: center;
  text-align: center;
  max-width: var(--text-max-width);

  ${PrimaryHeading} {
    margin-top: 0;
    margin-bottom: 32px;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      text-align: center;
      margin-bottom: 80px;
    }
  }

  ${SecondaryHeading} {
    margin-bottom: 30px;
  }
`

export const reduceEmptySpaces = css`
  ${PrimaryHeading} + p:empty {
    margin-top: -50px;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      margin-top: -90px;
    }
  }
  ${PrimaryHeading} + p:not(:empty) {
    margin-top: -10px;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      margin-top: -40px;
    }
  }

  ${SecondaryHeading} + p:empty {
    margin-top: -30px;
  }
`

export const textImageLayoutTabletGrid = css`
  grid-template-rows: auto auto 1fr;
  grid-template-columns: 1fr ${560 / 11.6}%;
  grid-column-gap: 40px;
`

// Layouts
const textImageLayout = css`
  grid: auto auto auto auto / 100%;
  grid-template-areas: "Heading" "Article" "Image" "ImageDescription";
  padding: 39px 0;

  p + p {
    margin-top: unset;
  }

  ${SectionImage} {
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      margin-top: 48px;
    }
  }

  ${SectionImageDescription} {
    margin-bottom: 2em;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    ${textImageLayoutTabletGrid};
    align-items: center;
    grid-template-areas:
      "Heading Heading"
      "Article Image"
      "span ImageDescription";

    ${SectionImageDescription} {
      text-align: right;
      margin-bottom: 0;
    }
  }

  @media (min-width: 1160px) {
    grid-column-gap: 120px;

    ${SectionArticle} {
    }

    ${SectionImageDescription} {
      margin-bottom: -30px;
    }
  }
`

const textImageWithOrderLayout = css`
  ${textImageLayout};
  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal - 1}px) {
    grid-template-areas: "Heading" "Article" "Image" "ImageDescription";
  }

  ${SectionImage} {
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      margin-top: 2em;
    }
  }
`

const imageTextLayout = css`
  grid: auto auto auto auto / 100%;
  grid-template-areas: "Heading" "Article" "Image" "ImageDescription";
  padding: 39px 0;

  p + p {
    margin-top: unset;
  }

  ${SectionImage} {
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      margin-top: 48px;
    }
  }

  ${SectionImageDescription} {
    margin-bottom: 2em;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    align-items: center;
    grid: auto 1fr auto / ${560 / 11.6}% 1fr;
    grid-column-gap: 40px;
    grid-template-areas:
      "Heading Heading"
      "Image Article"
      "ImageDescription span";

    ${SectionImageDescription} {
      margin-bottom: 0;
    }
  }

  @media (min-width: 1160px) {
    grid-column-gap: 90px;

    ${SectionArticle} {
      padding-right: 80px;
    }

    ${SectionImageDescription} {
      margin-bottom: -30px;
    }
  }
`

const imageTextBackgroundLayout = css`
  ${imageTextLayout};
  border-radius: ${({ theme }) => theme.size.borderRadius};
  background-color: ${({ theme }) => theme.color.background};

  ${SectionArticle} {
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      padding: 20px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoint.desktop - 1}px) {
      padding-right: 20px;
    }
  }

  ${SectionImage} {
    border-radius: 0;
    box-shadow: none;
  }
`

const centeredTextLayout = css`
  --text-max-width: 950px;
  grid: 1fr / 100%;
  grid-template-areas: "Heading" "Article" "SecondArticle";
  text-align: center;
  padding: 39px 0;

  ${SectionHeading} {
    width: 100%;
    h1,
    h2,
    h3,
    h4,
    h5,
    p,
    span {
      text-align: center !important;
    }
  }

  ${SectionArticle} {
    max-width: var(--text-max-width);
    strong {
      font-weight: 700;
    }
    a {
      margin-top: 2rem;
      @media (max-width: 639px) {
        /* width: 100%; */
      }
    }
    p {
      a {
        padding: 0;
        margin-top: 0;
      }
    }

    p {
      margin-top: 40px !important;

      &:last-child {
        margin-top: 0 !important;
      }
    }

    ${PrimaryHeading} {
      text-align: center;
      font-family: Facto;
      font-size: 30px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        font-size: 48px;
        margin-bottom: 30px;
      }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      &:first-of-type {
        padding-top: 0;
      }
      padding-bottom: 0;
      justify-self: center;
    }
  }
`

const textLeftAlignedLayout = css`
  ${centeredTextLayout};

  ${PrimaryHeading} {
    margin-bottom: 25px;
  }

  ${SectionArticle}, ${SectionHeading} {
    text-align: left;
    justify-self: flex-start;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    text-align: left;
  }
`

const twoTextsLayout = css`
  grid: auto auto auto 2em auto auto / 100%;
  grid-template-areas: "Heading" "LeftHeading" "LeftArticle" "." "RightHeading" "RightArticle";

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    align-content: start;
    grid: auto auto auto / 1fr 1fr;
    grid-column-gap: 30px;
    grid-template-areas:
      "Heading Heading"
      "LeftHeading RightHeading"
      "LeftArticle RightArticle";

    ${SectionArticle} {
      ${PrimaryHeading} {
        margin-top: 0;
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    /* padding-left: 25px; */
    /* padding-right: 25px; */
    grid-column-gap: 30px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
    /* padding-left: 50px;
    padding-right: 50px; */
    grid-column-gap: 60px;
  }

  & > ${SecondaryHeading} {
    margin-top: 0;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      margin-bottom: 0;
    }

    &:last-of-type {
      grid-area: RightHeading;
    }

    &:first-of-type {
      grid-area: LeftHeading;
    }
  }

  ${SectionArticle} {
    align-self: start;

    &:last-of-type {
      grid-area: RightArticle;
      @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
        ${Quote} {
          margin-right: -50px;
          margin-bottom: -25px;
        }
      }
    }

    &:first-of-type {
      grid-area: LeftArticle;
      @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
        ${Quote} {
          margin-left: -50px;
          margin-bottom: -25px;
        }
      }
    }
  }
`

const centeredTwoTextsLayout = css`
  ${twoTextsLayout};
  text-align: center;
`

export const articleWithoutBackground = css`
  padding: 0 0 50px 0;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    padding-bottom: 0;
  }
`

export const articleWithBackground = css`
  --background-outside-size: 100px;
  position: relative;
  align-self: stretch;
  padding: 0 30px;
  margin-bottom: calc(var(--background-outside-size) / 2);

  &:before {
    z-index: -1;
    position: absolute;

    top: calc(var(--background-outside-size) / -2);
    left: 0;
    width: 100%;
    height: calc(100% + var(--background-outside-size));

    content: "";
    display: block;

    border-radius: 4px;
    background-color: ${({ theme }) => theme.color.backgroundSuperLight};
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    --background-outside-size: 30px;
    padding: 0;

    &:before {
      left: calc(var(--background-outside-size) / -2);
      width: calc(100% + var(--background-outside-size));
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    --background-outside-size: 100px;
  }
`

const textTextLeftBackgroundLayout = css`
  ${twoTextsLayout};

  ${SectionArticle} {
    &:first-of-type {
      ${articleWithBackground};
    }
    &:last-of-type {
      ${articleWithoutBackground};
    }
  }
`

const textTextRightBackgroundLayout = css`
  ${twoTextsLayout};

  ${SectionArticle} {
    &:first-of-type {
      ${articleWithoutBackground};
    }
    &:last-of-type {
      ${articleWithBackground};
    }
  }
`

const textBigImageLayout = css`
  ${centeredTextLayout};
  grid: auto auto / 100%;
  grid-template-areas: "Heading" "Article" "Image" "ImageDescription";

  ${SectionArticle} {
    margin-bottom: 32px;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
      margin-bottom: 80px;
    }
  }

  ${SectionImageDescription} {
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      text-align: center;
    }
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      margin-top: 1em;
    }
  }

  > ${PrimaryHeading} {
    margin-top: 0;
    margin-bottom: ${50 / 30}em;

    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      margin-bottom: ${90 / 37}em;
    }
  }

  ${SectionHeading} {
    ${PrimaryHeading} {
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        margin-bottom: 50px;
      }
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    video {
      height: calc(100vw - 40px) !important;
      object-fit: cover;
    }
  }
`

const wideTextBigImageLayout = css`
  ${textBigImageLayout};
  grid: auto / 100%;
  grid-template-areas: "Heading" "Image" "Article" "ImageDescription";
  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-rows: calc(100vw - 40px) auto auto auto;
  }
`

const threeColsLayout = css`
  --text-max-width: 950px;
  grid-row-gap: 50px;
  grid-auto-rows: auto;
  justify-items: center;
  grid-template-columns: 100%;
  grid-template-areas: "Article";

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-column-gap: 40px;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas: "Article Article Article";
  }

  @media (min-width: 1160px) {
    grid-column-gap: 90px;
  }

  ${SectionArticle} {
    padding-top: 0;
    padding-bottom: 0;
    text-align: center;
    justify-self: center;
    max-width: var(--text-max-width);
  }

  > ${SectionArticle} {
    p {
      margin-bottom: 50px;
      @media (min-width: 960px) {
        margin-bottom: 90px;
      }
    }
  }
`

const fourColsLayout = css`
  ${threeColsLayout};

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-column-gap: 40px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas: "Article Article Article Article";
  }

  @media (min-width: 1160px) {
    grid-column-gap: 90px;
  }
`

const sixOnNineGridLayout = css`
  display: grid;
  grid-template-columns: repeat(3, 33.3%);

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-columns: repeat(7, 16.6%);
    grid-template-rows: repeat(7, 100px);
    grid-auto-rows: max-content;
  }

  section {
    display: unset;
    h2 {
      font-weight: 400;
    }
    p {
      margin-bottom: 50px;
      margin-top: 10px;
    }
  }
`

const twoColsLayout = css`
  ${threeColsLayout};

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-column-gap: 40px;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "Article Article";
  }

  @media (min-width: 1160px) {
    grid-column-gap: 72px;
  }
`

const newsletterLayout = css`
  ${centeredTextLayout}

  position: relative;
  padding-top: 90px;
  padding-bottom: 90px;
  background-color: ${({ theme }) => theme.color.backgroundAttentionLight};

  ${SectionArticle} {
    max-width: 700px;
    text-align: center;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal}px - 1) {
    padding-top: 120px;
    padding-bottom: 120px;
  }

  ${PrimaryHeading} {
    margin-bottom: 20px;
  }

  p {
    margin-top: 0 !important;
  }
`

const kickstarterNewsletterLayout = css<NewsletterBuilder["style"]>`
  ${newsletterLayout}
  /* background-color: ${({ theme }) =>
    theme.color.backgroundKickstarterNewsletter}; */
  padding-top: 150px;
  padding-bottom: 150px;
  /* button {
    background: ${({ theme }) => theme.color.kickstarterButton};
    border-radius: 5px;
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
  }
  button:hover {
    background: ${({ theme }) => theme.color.kickstarterButtonHover};
  } */
  ${SectionArticle} {
    max-width: 800px;
  }

  ${PrimaryHeading} {
    font-size: 30px;
    font-weight: 500;
  }

  ${NewsletterContainer} {
    max-width: 800px;
    width: 90%;
    p {
      font-size: 9px;
    }
  }
`
const kickstarterInlineNewsletterLayout = css`
  ${newsletterLayout}
  padding-top: 0;
  button {
    background: var(--link-color);
    border-radius: 5px;
    font-size: 14px;
  }
  button:hover {
    background: var(--link-color-hover);
  }

  ${SectionArticle} {
    margin: auto;
    text-align: center;
    justify-self: unset;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal}px - 1) {
    padding-top: 0px;
    padding-bottom: 100px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
    padding-left: 100px;
    padding-bottom: 100px;
  }

  ${PrimaryHeading} {
    font-size: 37px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    text-align: center;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      text-align: center;
    }
    @media (min-width: 900px) and (max-height: 800px) {
      font-size: 24px;
      margin-top: 15px !important;
    }
  }

  @media (min-width: 900px) and (max-height: 800px) {
    label {
      font-size: 14px;
    }
    button {
      font-size: 14px;
    }
  }

  ${NewsletterContainer} {
    width: 100%;
    max-width: 100%;
    margin: 20px 0 0 -5px;
    padding: 0;
    grid-template-areas:
      "Error Error Error Error"
      "Name Name Name Name"
      "Email Email Email Email"
      "Button Button Button Button"
      "Annotation Annotation Annotation Annotation";
    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
      grid-template-columns: 1.2fr 2fr 1.3fr;
      grid-template-areas:
        "Error Error Error"
        "Name Email Button"
        "Annotation Annotation Annotation";
    }
    p {
      text-align: center;
      margin-left: 10px;
      font-size: 9px;
      a {
        color: var(--link-color);
      }
    }
    button {
      max-width: 100%;
    }
  }
`

export enum SectionLayout {
  Text,
  TextLeftAligned,
  TextImage,
  TextImageWithOrder,
  ImageTextBackground,
  ImageText,
  TextText,
  CenteredTwoTexts,
  TextTextRightBackground,
  TextTextLeftBackground,
  TwoCols,
  ThreeCols,
  FourCols,
  TextBigImage,
  TextBigImageReverse,
  SixOnNine,
  Newsletter,
  KickstarterNewsletter,
  KickstarterInlineNewsletter,
}

const Section = styled.section<{
  layout?: SectionLayout
}>`
  display: grid;
  margin: 0 auto;
  position: relative;
  box-sizing: border-box;
  max-width: calc(var(--max-width) * 1px);
  z-index: 1;

  ${({ layout }) => {
    switch (layout) {
      case SectionLayout.Text:
        return centeredTextLayout
      case SectionLayout.TextLeftAligned:
        return textLeftAlignedLayout
      case SectionLayout.TextImage:
        return textImageLayout
      case SectionLayout.TextImageWithOrder:
        return textImageWithOrderLayout
      case SectionLayout.ImageText:
        return imageTextLayout
      case SectionLayout.ImageTextBackground:
        return imageTextBackgroundLayout
      case SectionLayout.TextText:
        return twoTextsLayout
      case SectionLayout.CenteredTwoTexts:
        return centeredTwoTextsLayout
      case SectionLayout.TextTextRightBackground:
        return textTextRightBackgroundLayout
      case SectionLayout.TextTextLeftBackground:
        return textTextLeftBackgroundLayout
      case SectionLayout.TextBigImage:
        return textBigImageLayout
      case SectionLayout.TextBigImageReverse:
        return wideTextBigImageLayout
      case SectionLayout.TwoCols:
        return twoColsLayout
      case SectionLayout.ThreeCols:
        return threeColsLayout
      case SectionLayout.FourCols:
        return fourColsLayout
      case SectionLayout.SixOnNine:
        return sixOnNineGridLayout
      case SectionLayout.Newsletter:
        return newsletterLayout
      case SectionLayout.KickstarterNewsletter:
        return kickstarterNewsletterLayout
      case SectionLayout.KickstarterInlineNewsletter:
        return kickstarterInlineNewsletterLayout
      default:
        return ""
    }
  }}
`

// Section sizes
export const SmallSection = styled(Section)`
  --max-width: 960;
  /* width: calc(100% - 40px); */
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    width: calc(100% - 60px);
  }
`

export const StandardSection = styled(Section)`
  --max-width: 1260;
  width: calc(100% - 40px);
`

export const NavigationSection = styled(Section)`
  --max-width: 1260;
  width: calc(100% - 40px);
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    width: calc(100% - 60px);
  }
`

export const MediumSection = styled(Section)`
  --max-width: 1260;
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    width: calc(100% - 40px);
  }
`

export const FullSection = styled(Section)`
  --max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  @media (min-width: ${({ theme }) => theme.breakpoint.desktopMedium}px) {
    padding-left: calc((100% - 1260px) / 2);
    padding-right: calc((100% - 1260px) / 2);
  }
`

export const FullSectionLandingKickstarter = styled(Section)<{
  customStyle: NewsletterBuilder["style"]
}>`
  /* --link-color: ${({ customStyle }) =>
    customStyle.linkColor ?? "inherit"}; */
  --link-color-hover: ${({ customStyle }) =>
    customStyle.linkColorHover ?? "inherit"};
  --max-width: 100%;
  --article-max-width: ${({ customStyle }) =>
    customStyle.articleMaxWidth ?? "inherit"};
  max-width: 1280px;
  width: 100%;
  background-color: ${({ theme, customStyle }) =>
    customStyle.backgroundColor ?? theme.color.kickstarterBackgroundPrimary};
  padding-left: 20px;
  padding-right: 20px;
  div,
  p {
    color: ${({ customStyle }) => customStyle.textColor ?? ""};
  }

  ${SectionArticle} {
    max-width: var(--article-max-width);
  }
  @media (min-width: ${({ theme }) =>
      theme.breakpoint.tablet}px) and (max-width: ${({ theme }) =>
      theme.breakpoint.desktopMedium}px) {
    padding-left: 50px;
  }
`
export const FullSectionLandingKickstarterWrapper = styled.div<{
  customStyle: NewsletterBuilder["style"]
}>`
  width: 100%;
  background-color: ${({ theme, customStyle }) =>
    customStyle?.backgroundColor ?? theme.color.kickstarterBackgroundPrimary};
  padding-top: 90px;
  padding-bottom: 30px;
  /* @media (min-height: 800px) and (max-height: 890px) {
    padding-top: 1rem;
  }
  @media (min-height: 890px) and (max-height: 930px) {
    padding-top: 0rem;
  }
  @media (min-height: 930px) {
    padding-top: 0rem;
    padding-bottom: 1rem;
  } */
`

export const BackgroundWrapper = styled(FullSection)<{
  background?: string
  no_space_below?: boolean
  paddingTopModifier?: number
  paddingBottomModifier?: number
  backgroundCropped?: boolean
}>`
  padding-top: var(--grid-spacing);
  padding-bottom: var(--grid-spacing);
  background-color: ${({ background }) => background};
  ${({ no_space_below }) =>
    no_space_below &&
    css`
      margin-bottom: calc(-1 * (var(--grid-spacing) * 1.6));
    `}
  ${({ paddingTopModifier }) =>
    paddingTopModifier &&
    css`
      padding-top: calc(var(--grid-spacing) * ${paddingTopModifier});
    `}
    ${({ paddingBottomModifier }) =>
    paddingBottomModifier &&
    css`
      padding-bottom: calc(var(--grid-spacing) * ${paddingBottomModifier});
    `}

    ${({ backgroundCropped }) =>
    backgroundCropped &&
    css`
      --max-width: 1260;
      width: calc(100% - 40px);
      max-width: calc(var(--max-width) * 1px);
      padding: 40px 32px;
      & > div {
        padding: 0;
      }

      @media (min-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        padding: 72px 64px;
      }
    `}

  ${Section} {
    width: 100%;
  }
`

export default StandardSection
